import { Grid, SvgIcon, Tooltip } from '@mui/material';
import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import NewWindow from 'assets/icons/NewWindow.svg?react';
import PdfIcon from 'assets/icons/pdf-icon.svg?react';
import RedistributeIcon from 'assets/icons/redistribute-document.svg?react';
import AGGridComponent from 'components/AgGrid/AGGridComponent';
import { dateTimeValueFormatter } from 'components/AgGrid/gridComponents/helpers';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { humanReadableString } from 'utils/stringUtils';

import { DocumentV2 } from 'pco-product-service-internal-consumer';
import { getDocument } from '../../../thunks/productDocumentsThunk';
import { DocumentRedistributionModal } from './DocumentRedistributionModal';
import { getDocumentFromApi } from 'services/documents/getDocumentFromApi';
import { JBColors } from '@bjb-gcrm/gcrm-frontend-components';
import { closeModal, openModal } from '../../../reducers/modalReducer';

// TODO -> render icon if status not yet_to_distribute || pending

type Props = {
  documents: DocumentV2[];
};

const Documents = ({ documents }: Props) => {
  const [isRedistributionModalOpen, setIsRedistributionModalOpen] = useState(false);
  const [selectedDocumentUid, setSelectedDocumentUid] = useState<string | undefined>(undefined);
  const dispatch = useDispatch<AppDispatch>();
  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);

  const getDocumentAndOpen = (serviceAgreementId: string, documentId: string, documentName: string) => {
    dispatch(getDocument(serviceAgreementId, documentId, documentName));
  };

  const handleRedistributeIconClick = (documentUid: string | undefined) => {
    setSelectedDocumentUid(documentUid);
    setIsRedistributionModalOpen(true);
  };

  const handleRedirect = async (params: ICellRendererParams<DocumentV2>) => {
    if (!params.data) return;
    const { documentRedirectUrl, documentUid } = params.data;

    if (!selectedServiceAgreement?.serviceAgreementId || !documentUid) {
      return;
    }

    try {
      const actualPcdDocumentId = await getDocumentFromApi(selectedServiceAgreement.serviceAgreementId, documentUid);
      if (actualPcdDocumentId) {
        const url = `${documentRedirectUrl}?documentId=${actualPcdDocumentId}`;
        window.open(url);
      }
    } catch (error: unknown) {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when retrieving documents',
              userErrorMessage: 'An error occurred when retrieving documents. Please try again later.',
            },
          },
        }),
      );
    }
  };

  const columnDefs: (ColDef | ColGroupDef)[] = [
    {
      headerName: 'Document Id',
      field: 'documentId',
      hide: true,
    },
    {
      headerName: 'Document',
      field: 'documentName',
    },
    {
      headerName: 'Last Updated',
      field: 'documentLastUpdated',
      cellRenderer: (params: ICellRendererParams<DocumentV2>) => {
        const date = dateTimeValueFormatter({ value: params.value });

        if (!date) return null;

        const cellValue = date.toString();
        const cellWidth = params.column?.getActualWidth() || 0;

        // Create a temporary span element to measure the text width
        const tempSpan = document.createElement('span');
        tempSpan.innerText = cellValue;
        tempSpan.style.visibility = 'hidden';
        document.body.appendChild(tempSpan);

        // Check if the text width is greater than the cell width
        const cellContentWidth = tempSpan.offsetWidth > cellWidth - 30;

        // Remove the temporary span element
        document.body.removeChild(tempSpan);

        if (cellContentWidth) {
          return (
            <Tooltip title={cellValue} arrow>
              <span>{cellValue}</span>
            </Tooltip>
          );
        } else {
          return <span>{cellValue}</span>;
        }
      },
      minWidth: 90,
    },
    {
      headerName: 'Status',
      field: 'documentStatus',
      cellRenderer: (params: ICellRendererParams<DocumentV2>) => humanReadableString(params.value),
      minWidth: 100,
    },
    {
      headerName: '',
      filter: false,
      field: 'documentId',
      cellRenderer: (params: ICellRendererParams<DocumentV2>) => {
        if (params.data?.documentStatus !== 'YET_TO_DISTRIBUTE' && selectedServiceAgreement)
          return (
            <Grid container sx={{ display: 'flex', justifyContent: 'end' }}>
              <SvgIcon
                sx={{ cursor: 'pointer', mt: 0.5 }}
                onClick={() =>
                  getDocumentAndOpen(
                    selectedServiceAgreement?.serviceAgreementId ?? '',
                    params.data?.documentUid ?? '',
                    params.data?.documentName ?? '',
                  )
                }
              >
                <PdfIcon height={'1em'} width={'1em'} />
              </SvgIcon>
            </Grid>
          );
        else return null;
      },
      width: 50,
      maxWidth: 50,
    },
    {
      headerName: '',
      filter: false,
      field: 'documentRedirectUrl',
      cellRenderer: (params: ICellRendererParams<DocumentV2>) => {
        if (params?.data?.documentStatus !== 'YET_TO_DISTRIBUTE')
          return (
            <Grid container sx={{ display: 'flex', justifyContent: 'end' }}>
              <SvgIcon sx={{ cursor: 'pointer', mt: 0.5 }} onClick={() => handleRedirect(params)}>
                <NewWindow />
              </SvgIcon>
            </Grid>
          );
        else return null;
      },
      maxWidth: 50,
    },
    {
      headerName: '',
      filter: false,
      field: 'redistributeButton',
      cellRenderer: (params: ICellRendererParams<DocumentV2>) => {
        return (
          <Grid container sx={{ display: 'flex', justifyContent: 'end' }}>
            {params?.data?.canRedistribute && params?.data?.documentUid && (
              <SvgIcon
                sx={{
                  color: JBColors.core.reflexBlue[100],
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  mt: 0.5,
                }}
                onClick={() => handleRedistributeIconClick(params.data?.documentUid)}
                id="common-dialog-close"
              >
                <RedistributeIcon height={'1em'} width={'1em'} />
              </SvgIcon>
            )}
          </Grid>
        );
      },
      maxWidth: 70,
    },
  ];

  return (
    <>
      <Grid data-testid="documents" container item xs={12} sx={{ display: 'flex', height: '100%', width: '100%' }}>
        <AGGridComponent
          id={'documents'}
          data-testid="documents-table"
          disableSelection={true}
          rowKey={'documentUid'}
          getColumnDefs={columnDefs}
          items={documents as unknown as Record<string, unknown>[]}
        />
      </Grid>
      {isRedistributionModalOpen && (
        <DocumentRedistributionModal
          isOpen={isRedistributionModalOpen}
          setIsOpen={setIsRedistributionModalOpen}
          documentUid={selectedDocumentUid}
        />
      )}
    </>
  );
};

export default Documents;
